import React, { useState, useEffect, useRef } from 'react';
import { 
  Save, 
  ArrowLeft,
  Loader,
  FileText,
  X
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLessonContext } from '../../contexts/LessonContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import { Lesson } from '../../types';
import { ResourceSelector } from './ResourceSelectorComponents';
import { lessonService } from '../../services/lessonService';
import toast from 'react-hot-toast';
import { 
  saveDraft, 
  loadDraft, 
  clearDraft, 
  getDraftMetadata, 
  formatDraftDate 
} from '../../utils/draftStorage';
import {
  LessonMetadataSection,
  LearningObjectivesSection,
  MaterialsSection,
  TimelineStepsSection,
  AssessmentsSection,
  DifferentiationSection,
  DataAndReportingSection,
  ImportExportSection
} from './LessonFormComponents';

interface LessonFormProps {
  mode: 'create' | 'edit';
}

export const LessonForm: React.FC<LessonFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { lessonId } = useParams<{ lessonId: string }>();
  const { state, dispatch } = useLessonContext();
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState<Partial<Lesson>>({
    metadata: {
      lesson_id: '',
      title: '',
      subject: '',
      domain: '',
      construct: '',
      subconstruct: '',
      global_minimum_proficiency: '',
      grade: "1",
      language: 'en',
      version: '1.0.0',
      duration_minutes: 30,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    className: '',
    objectives: [''],
    success_criteria: [''],
    standards_alignment: [''],
    prerequisites: '',
    materials: {
      digital: [''],
      physical_alternatives: ['']
    },
    resources: {},
    timeline: [],
    assessments: {},
    misconceptions_and_responses: [],
    differentiation: {
      supports: {},
      extensions: {},
      ell: {}
    },
    classroom_management_notes: [''],
    data_and_reporting: {
      events: [],
      next_lesson_grouping_rule: ''
    },
    offline_variant: {
      directions: ['']
    },
    home_connection: {
      family_note: ''
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [importing, setImporting] = useState(false);
  const [draftMetadata, setDraftMetadata] = useState<{ timestamp: number; title?: string } | null>(null);
  const [showDraftRestore, setShowDraftRestore] = useState(false);
  const [formKey, setFormKey] = useState(0); // Clé pour forcer le re-render
  const draftSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);
  const isRestoringRef = useRef(false); // Flag pour indiquer qu'on restaure le brouillon

  // Restaurer le brouillon en mode création - DOIT être exécuté AVANT la sauvegarde automatique
  useEffect(() => {
    if (mode === 'create' && isInitialLoadRef.current) {
      const draft = loadDraft();
      const metadata = getDraftMetadata();
      
      if (draft && metadata) {
        // Restaurer immédiatement le brouillon au chargement
        isRestoringRef.current = true;
        
        // Valeurs par défaut pour fusionner avec le brouillon
        const defaultValues: Partial<Lesson> = {
          metadata: {
            lesson_id: '',
            title: '',
            subject: '',
            domain: '',
            construct: '',
            subconstruct: '',
            global_minimum_proficiency: '',
            grade: "1",
            language: 'en',
            version: '1.0.0',
            duration_minutes: 30,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
          className: '',
          objectives: [''],
          success_criteria: [''],
          standards_alignment: [''],
          prerequisites: '',
          materials: {
            digital: [''],
            physical_alternatives: ['']
          },
          resources: {},
          timeline: [],
          assessments: {},
          misconceptions_and_responses: [],
          differentiation: {
            supports: {},
            extensions: {},
            ell: {}
          },
          classroom_management_notes: [''],
          data_and_reporting: {
            events: [],
            next_lesson_grouping_rule: ''
          },
          offline_variant: {
            directions: ['']
          },
          home_connection: {
            family_note: ''
          }
        };
        
        const restoredData: Partial<Lesson> = JSON.parse(JSON.stringify({
          ...defaultValues,
          ...draft,
          metadata: {
            ...defaultValues.metadata,
            ...draft.metadata,
            lesson_id: '', // Toujours vide pour une nouvelle leçon
          },
          objectives: draft.objectives && draft.objectives.length > 0 ? draft.objectives : defaultValues.objectives,
          success_criteria: draft.success_criteria && draft.success_criteria.length > 0 ? draft.success_criteria : defaultValues.success_criteria,
          standards_alignment: draft.standards_alignment && draft.standards_alignment.length > 0 ? draft.standards_alignment : defaultValues.standards_alignment,
          materials: draft.materials || defaultValues.materials,
          resources: draft.resources || defaultValues.resources,
          timeline: draft.timeline && draft.timeline.length > 0 ? draft.timeline : defaultValues.timeline,
          assessments: draft.assessments || defaultValues.assessments,
          misconceptions_and_responses: draft.misconceptions_and_responses && draft.misconceptions_and_responses.length > 0 ? draft.misconceptions_and_responses : defaultValues.misconceptions_and_responses,
          differentiation: draft.differentiation || defaultValues.differentiation,
          classroom_management_notes: draft.classroom_management_notes && draft.classroom_management_notes.length > 0 ? draft.classroom_management_notes : defaultValues.classroom_management_notes,
          data_and_reporting: draft.data_and_reporting || defaultValues.data_and_reporting,
          offline_variant: draft.offline_variant || defaultValues.offline_variant,
          home_connection: draft.home_connection || defaultValues.home_connection
        }));
        
        setFormData(() => restoredData);
        setDraftMetadata(metadata);
        setFormKey(prev => prev + 1);
        
        // Réinitialiser le flag après un court délai pour permettre à la restauration de se terminer
        setTimeout(() => {
          isRestoringRef.current = false;
          isInitialLoadRef.current = false;
        }, 100);
      } else {
        // Pas de brouillon, on peut activer la sauvegarde automatique
        isInitialLoadRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (mode === 'edit' && lessonId) {
      // En mode édition, on ne veut pas de brouillon
      clearDraft();
      setDraftMetadata(null);
      
      // If no lessons are loaded in context, load them first
      if (state.lessons.length === 0) {
        setLoadingLesson(true);
        const loadLessons = async () => {
          try {
            const lessons = await lessonService.getAllLessons();
            dispatch({ type: 'SET_LESSONS', payload: lessons });
          } catch (error) {
            console.error('Error loading lessons:', error);
          } finally {
            setLoadingLesson(false);
          }
        };
        loadLessons();
      }

      const lesson = state.lessons.find(l => l.metadata.lesson_id === lessonId);
      if (lesson) {
        setFormData(lesson);
      } else {
        // If lesson not found in context, try to fetch it from service
        const fetchLesson = async () => {
          try {
            const fetchedLesson = await lessonService.getLessonById(lessonId);
            if (fetchedLesson) {
              setFormData(fetchedLesson);
            }
          } catch (error) {
            console.error('Error fetching lesson for edit:', error);
          }
        };
        fetchLesson();
      }
    }
  }, [mode, lessonId, state.lessons, dispatch]);

  // Sauvegarder automatiquement le brouillon en mode création
  useEffect(() => {
    // Ne pas sauvegarder pendant le chargement initial ou la restauration
    if (mode === 'create' && !isInitialLoadRef.current && !isRestoringRef.current) {
      // Débounce la sauvegarde pour éviter trop d'écritures
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
      }
      
      draftSaveTimeoutRef.current = setTimeout(() => {
        saveDraft(formData);
        const metadata = getDraftMetadata();
        setDraftMetadata(metadata);
      }, 1000); // Sauvegarder après 1 seconde d'inactivité

      return () => {
        if (draftSaveTimeoutRef.current) {
          clearTimeout(draftSaveTimeoutRef.current);
        }
      };
    }
  }, [formData, mode]);

  // Sauvegarder le brouillon avant de quitter la page
  useEffect(() => {
    if (mode === 'create') {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        // Sauvegarder immédiatement le brouillon
        if (draftSaveTimeoutRef.current) {
          clearTimeout(draftSaveTimeoutRef.current);
        }
        console.log(formData);
        saveDraft(formData);
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [formData, mode]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.metadata?.title?.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.metadata?.subject) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.metadata?.grade) {
      newErrors.grade = 'Grade is required';
    }
    if (!formData.metadata?.duration_minutes || formData.metadata.duration_minutes <= 0) {
      newErrors.duration = 'Duration must be greater than 0';
    }
    if (!formData.objectives?.length || !formData.objectives[0]?.trim()) {
      newErrors.objectives = 'At least one objective is required';
    }
    if (!formData.timeline?.length) {
      newErrors.timeline = 'At least one timeline step is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitCreate = async (e: React.FormEvent): Promise<string | null> => {
    e.preventDefault();
    
    if (!validateForm()) {
      return null;
    }
    // Create lesson without ID - Firebase will generate one
    const lessonData = {
      ...formData,
      metadata: {
        ...formData.metadata!,
        lesson_id: '', // Will be set by Firebase
        created_at: new Date().toISOString(),
        createdBy: user?.id, // Add creator ID
        isAdminLesson: user?.isAdmin ? (formData.metadata?.isAdminLesson || false) : false // Only admins can create admin lessons
      }
    } as Lesson;
    
    // Save to lesson service and get Firebase-generated ID
    const firebaseId = await lessonService.createLesson(lessonData);
    
    // Update the lesson data with the Firebase ID
    const createdLesson = {
      ...lessonData,
      metadata: {
        ...lessonData.metadata,
        lesson_id: firebaseId
      }
    } as Lesson;
    
    // Add to context with Firebase ID
    dispatch({ type: 'ADD_LESSON', payload: createdLesson });
    
    // Show success message and navigate
    toast.success('Lesson created successfully!');
    return firebaseId;
  }

  const handleSubmitUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return null;
    }

    const lessonId = formData.metadata!.lesson_id

    // Update existing lesson
    const updatedLesson = {
      ...formData,
      metadata: {
        ...formData.metadata!,
        lesson_id: lessonId,
        updated_at: new Date().toISOString()
      }
    } as Lesson;
    
    await lessonService.updateLesson(lessonId, updatedLesson);
    dispatch({ type: 'UPDATE_LESSON', payload: updatedLesson });
    
    toast.success('Lesson updated successfully!');
    return lessonId;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (mode === 'create') {
        const firebaseId = await handleSubmitCreate(e);
        if (!firebaseId) return;
        // Vider le brouillon après sauvegarde réussie
        clearDraft();
        setDraftMetadata(null);
        navigate(`/session/${firebaseId}`);
      } else if (lessonId) {
        const result = await handleSubmitUpdate(e);
        if (!result) return;
        navigate(`/session/${lessonId}`);
      }
    } catch (error) {
      console.error('Error saving lesson:', error);
      toast.error('Error saving lesson. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreDraft = () => {
    const draft = loadDraft();
    if (draft) {
      // Empêcher la sauvegarde automatique pendant la restauration
      isRestoringRef.current = true;
      
      // Créer une copie profonde pour forcer React à détecter le changement
      const restoredData: Partial<Lesson> = JSON.parse(JSON.stringify({
        ...formData, // Valeurs par défaut
        ...draft, // Données du brouillon
        metadata: {
          lesson_id: '',
          title: draft.metadata?.title || formData.metadata?.title || '',
          subject: draft.metadata?.subject || formData.metadata?.subject || '',
          domain: draft.metadata?.domain || formData.metadata?.domain || '',
          construct: draft.metadata?.construct || formData.metadata?.construct || '',
          subconstruct: draft.metadata?.subconstruct || formData.metadata?.subconstruct || '',
          global_minimum_proficiency: draft.metadata?.global_minimum_proficiency || formData.metadata?.global_minimum_proficiency || '',
          grade: draft.metadata?.grade || formData.metadata?.grade || '1',
          language: draft.metadata?.language || formData.metadata?.language || 'en',
          version: draft.metadata?.version || formData.metadata?.version || '1.0.0',
          duration_minutes: draft.metadata?.duration_minutes || formData.metadata?.duration_minutes || 30,
          created_at: draft.metadata?.created_at || formData.metadata?.created_at || new Date().toISOString(),
          updated_at: draft.metadata?.updated_at || formData.metadata?.updated_at || new Date().toISOString(),
          createdBy: draft.metadata?.createdBy || formData.metadata?.createdBy,
          isAdminLesson: draft.metadata?.isAdminLesson || formData.metadata?.isAdminLesson || false,
        },
        // S'assurer que les tableaux et objets sont bien définis
        objectives: draft.objectives && draft.objectives.length > 0 ? draft.objectives : (formData.objectives || ['']),
        success_criteria: draft.success_criteria && draft.success_criteria.length > 0 ? draft.success_criteria : (formData.success_criteria || ['']),
        standards_alignment: draft.standards_alignment && draft.standards_alignment.length > 0 ? draft.standards_alignment : (formData.standards_alignment || ['']),
        materials: draft.materials || formData.materials || {
          digital: [''],
          physical_alternatives: ['']
        },
        resources: draft.resources || formData.resources || {},
        timeline: draft.timeline && draft.timeline.length > 0 ? draft.timeline : (formData.timeline || []),
        assessments: draft.assessments || formData.assessments || {},
        misconceptions_and_responses: draft.misconceptions_and_responses && draft.misconceptions_and_responses.length > 0 ? draft.misconceptions_and_responses : (formData.misconceptions_and_responses || []),
        differentiation: draft.differentiation || formData.differentiation || {
          supports: {},
          extensions: {},
          ell: {}
        },
        classroom_management_notes: draft.classroom_management_notes && draft.classroom_management_notes.length > 0 ? draft.classroom_management_notes : (formData.classroom_management_notes || ['']),
        data_and_reporting: draft.data_and_reporting || formData.data_and_reporting || {
          events: [],
          next_lesson_grouping_rule: ''
        },
        offline_variant: draft.offline_variant || formData.offline_variant || {
          directions: ['']
        },
        home_connection: draft.home_connection || formData.home_connection || {
          family_note: ''
        }
      }));
      
      // Utiliser une fonction de mise à jour pour garantir que React détecte le changement
      setFormData(() => restoredData);
      
      // Incrémenter la clé pour forcer le re-render de tous les composants enfants
      setFormKey(prev => prev + 1);
      
      // Mettre à jour les métadonnées du brouillon
      const metadata = getDraftMetadata();
      setDraftMetadata(metadata);
      
      // Réinitialiser les flags après un court délai
      setTimeout(() => {
        isRestoringRef.current = false;
        isInitialLoadRef.current = false;
      }, 100);
      
      setShowDraftRestore(false);
      toast.success('Brouillon restauré');
    }
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setDraftMetadata(null);
    setShowDraftRestore(false);
    toast.success('Brouillon supprimé');
  };

  const handleResourcesChange = (resources: Record<string, any>) => {
    setFormData(prev => ({ ...prev, resources }));
  };



  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.preventDefault();
    }
  };

  if (loadingLesson) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">{t('lesson.loadingLesson')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`${lessonId ? `/session/${lessonId}` : '/lessons'}`)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                {mode === 'create' ? t('lesson.createNew') : t('lesson.editLesson')}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              {mode === 'create' && draftMetadata && (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">
                    Brouillon sauvegardé {formatDraftDate(draftMetadata.timestamp)}
                  </span>
                  <span className="sm:hidden">Brouillon</span>
                </div>
              )}
              
              <button
                type="submit"
                form="lesson-form"
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{loading ? t('actions.saving') : t('lesson.saveLesson')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Banner de restauration du brouillon */}
      {mode === 'create' && showDraftRestore && draftMetadata && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    Un brouillon a été trouvé
                  </p>
                  <p className="text-xs text-blue-700">
                    {draftMetadata.title ? `"${draftMetadata.title}"` : 'Sans titre'} - {formatDraftDate(draftMetadata.timestamp)}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleRestoreDraft}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Restaurer
                </button>
                <button
                  onClick={handleDiscardDraft}
                  className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                  title="Ignorer le brouillon"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form 
          key={formKey}
          id="lesson-form"
          onSubmit={handleSubmit}
          onKeyDown={handleKeyDown}
          className="space-y-8"
        >
          {/* Import/Export Section */}
          <ImportExportSection
            formData={formData}
            setFormData={setFormData}
            importing={importing}
            setImporting={setImporting}
          />

          {/* Lesson Metadata Section */}
          <LessonMetadataSection
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />

          {/* Learning Objectives Section */}
          <LearningObjectivesSection
            formData={formData}
            setFormData={setFormData}
            errors={errors}
          />

          {/* Admin Section - Only visible to admins */}
          {user?.isAdmin && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('lesson.adminSettings')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isAdminLesson"
                    checked={formData.metadata?.isAdminLesson || false}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      metadata: {
                        ...prev.metadata!,
                        isAdminLesson: e.target.checked
                      }
                    }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isAdminLesson" className="ml-2 block text-sm text-gray-900">
                    {t('lesson.markAsAdminLesson')}
                  </label>
                </div>
                <p className="text-sm text-gray-600">
                  {t('lesson.adminLessonDescription')}
                </p>
              </div>
            </div>
          )}

          {/* Materials Section */}
          <MaterialsSection
            formData={formData}
            setFormData={setFormData}
          />

          {/* Resources */}
          <ResourceSelector
            resources={formData.resources || {}}
            onResourcesChange={handleResourcesChange}
          />

          {/* Timeline Steps Section */}
          <TimelineStepsSection
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            onSave={handleSubmitUpdate}
          />

          {/* Assessments Section */}
          <AssessmentsSection
            formData={formData}
            setFormData={setFormData}
          />

          {/* Differentiation Section */}
          <DifferentiationSection
            differentiation={formData.differentiation || { supports: {}, extensions: {}, ell: {} }}
            onUpdate={(differentiation) => setFormData(prev => ({ 
              ...prev, 
              differentiation: {
                supports: differentiation.supports || {},
                extensions: differentiation.extensions || {},
                ell: differentiation.ell || {}
              }
            }))}
          />

          {/* Data & Reporting Section */}
          <DataAndReportingSection
            dataAndReporting={formData.data_and_reporting || { events: [], next_lesson_grouping_rule: '' }}
            onUpdate={(dataAndReporting) => setFormData(prev => ({ 
              ...prev, 
              data_and_reporting: {
                events: dataAndReporting.events || [],
                next_lesson_grouping_rule: dataAndReporting.next_lesson_grouping_rule || '',
                analytics_enabled: dataAndReporting.analytics_enabled || false,
                student_tracking: dataAndReporting.student_tracking || {
                  progress_tracking: false,
                  time_tracking: false,
                  engagement_metrics: false
                },
                reporting_frequency: dataAndReporting.reporting_frequency || 'end_of_lesson',
                export_formats: dataAndReporting.export_formats || []
              }
            }))}
          />
        </form>
      </div>

      {/* Footer */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`${lessonId ? `/session/${lessonId}` : '/lessons'}`)}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                {mode === 'create' ? t('lesson.createNew') : t('lesson.editLesson')}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                type="submit"
                form="lesson-form"
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{loading ? t('actions.saving') : t('lesson.saveLesson')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
